import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Practitioner } from 'src/app/shared/models/practitioner.model';
import { Specialty } from 'src/app/shared/models/specialty.model';
import { Therapy } from 'src/app/shared/models/therapy.model';
import { LookupService } from 'src/app/shared/services/lookup.service';

@Component({
  selector: 'app-practitioner-edit-services',
  templateUrl: './practitioner-edit-services.component.html',
  styleUrls: ['./practitioner-edit-services.component.scss']
})
export class PractitionerEditServicesComponent implements OnInit {
  formGroup: FormGroup;
  bodySpecialties: string[] = [];
  mindSpecialties: string[] = [];

  @Input() isLoading: boolean;
  @Input() practitioner: Practitioner;
  @Output() dismissed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<FormGroup>();

  constructor(private lookupService: LookupService, private cd: ChangeDetectorRef) { }

  ngOnInit(): void {
    this._loadDropdowns();
  }

  _loadDropdowns(): void {
    this.lookupService.searchSpecialties('body').then(values => {
      this.bodySpecialties = values;
      this.bodySpecialties.unshift('Select...');
      this.bodySpecialties.push('Other...');
      if (this.mindSpecialties.length > 0) { this._createFormGroup(); }
      this.cd.detectChanges();
    });

    this.lookupService.searchSpecialties('mind').then(values => {
      this.mindSpecialties = values;
      this.mindSpecialties.unshift('Select...');
      this.mindSpecialties.push('Other...');
      if (this.bodySpecialties.length > 0) { this._createFormGroup(); }
      this.cd.detectChanges();
    });
  }

  _createFormGroup(): void {
    this.formGroup = new FormGroup({
      'categories': this.loadFormArrays(this.practitioner.specialties.map(s => s.category), [Validators.required]),
      'specialties': this.loadFormArrays(this.practitioner.specialties.map(s => s.name), [this.containsSelect.bind(this)]),
      'specialtyData': this.loadSpecialtyData()
    });

    setTimeout(() => {
      this.practitioner.specialties.map(s => s.category).forEach((category, index) => {
        let elem: HTMLSelectElement = document.getElementById('categorySelect' + index) as HTMLSelectElement;
        this.cd.detectChanges();
        elem.value = category;
      });
      this.practitioner.specialties.map(s => s.name).forEach((name, index) => {
        let elem: HTMLSelectElement = document.getElementById('nameSelect' + index) as HTMLSelectElement;
        this.cd.detectChanges();
        elem.value = name;
      });
    }, 100);
  }

  onSubmit(): void {
    if (!this.formGroup.valid) { return; }
    let finalFormGroup = new FormGroup({
      'specialties': new FormControl(this._mapSpecialties())
    });
    if (this.formGroup.dirty) {
      finalFormGroup.get('specialties').markAsDirty();
    }

    this.submitted.emit(finalFormGroup);
  }

  loadSpecialtyData(): FormArray {
    let formArray: FormArray = new FormArray([]);
    this.practitioner.specialties.forEach(specialty => {
      let dataFormGroup: FormGroup = new FormGroup({
        'therapies': this.loadFormArrays(specialty.therapies.map(t => t.name), [Validators.required]),
        'costs': this.loadFormArrays(specialty.therapies.map(t => t.cost), [Validators.required, Validators.min(1)]),
        'descriptions': this.loadFormArrays(specialty.therapies.map(t => t.description), [Validators.required]),
        'durations': this.loadFormArrays(specialty.therapies.map(t => t.duration), [Validators.required, Validators.min(1)]),
      });
      formArray.push(dataFormGroup);
    });
    return formArray;
  }

  loadFormArrays(values: any[], validators: ValidatorFn[]): FormArray {
    let formArray: FormArray = new FormArray([]);
    values.forEach((value, index) => {
      formArray.push(new FormControl(value, validators))
    });
    return formArray;
  }

  _mapSpecialties(): Specialty[] {
    var specialties: Specialty[] = [];
    var value = this.formGroup.value;
    value.specialties.forEach((specialty: string, sIndex: number) => {
      var therapies: Therapy[] = [];
      var category: string = value.categories[sIndex];
      value.specialtyData[sIndex].therapies.forEach((name: string, tIndex: number) => {
        therapies.push(new Therapy(
          name,
          value.specialtyData[sIndex].costs[tIndex],
          value.specialtyData[sIndex].descriptions[tIndex],
          value.specialtyData[sIndex].durations[tIndex]));
      })
      specialties.push(new Specialty(category, specialty, therapies));
    });

    return specialties;
  }

  getControls(type: string): AbstractControl[] {
    return (<FormArray>this.formGroup.get(type)).controls;
  }

  onAddControl(type: string) {
    (<FormArray>this.formGroup.get(type)).push(new FormControl(null));
    this.formGroup.get(type).markAsDirty();
  }

  onRemoveControl(type: string, index: number) {
    (<FormArray>this.formGroup.get(type)).removeAt(index);
    this.formGroup.get(type).markAsDirty();
  }

  onAddSpecialtyDataControl() {
    (<FormArray>this.formGroup.get('specialtyData')).push(new FormGroup({
      'therapies': new FormArray([
        new FormControl(null, Validators.required)
      ]),
      'costs': new FormArray([
        new FormControl(null, [Validators.required, Validators.min(1)])
      ]),
      'descriptions': new FormArray([
        new FormControl(null, Validators.required)
      ]),
      'durations': new FormArray([
        new FormControl(null, [Validators.required, Validators.min(1)])
      ])
    }));
  }

  updateSpecialtyCategory(index: number, value: string) {
    let currentCategories: string[] = [...this.formGroup.value.categories];
    currentCategories[index] = value;
    this.formGroup.patchValue({
      categories: currentCategories
    });
  }

  updateSpecialty(index: number, value: string) {
    let isOtherSpecialty = value.toLowerCase() === 'other...';
    let newValue = isOtherSpecialty ? '' : value;
    let currentSpecialties: string[] = [...this.formGroup.value.specialties];
    currentSpecialties[index] = newValue;
    this.formGroup.patchValue({
      specialties: currentSpecialties
    });
    document.getElementById('specialty' + index).hidden = !isOtherSpecialty;
  }

  containsSelect(control: FormControl): {[s:string]: boolean} {
    if (control.value && control.value.toLowerCase().indexOf('select') > -1) {
      return {'containsSelect': true};
    }
    return null;
  }

  onDismiss(): void {
    this.dismissed.emit();
  }
}
